import * as React from 'react'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { EditableCustomField } from '@user/components/CustomFields'
import { selectAllCustomFields } from '@user/store/customFields'
import type { ICustomFieldFormSetting } from '@user/store/entityFormSettings'

interface Props {
  fieldId: number
}

/**
 * Renders one missing custom field with the shared EditableCustomField. The definition comes from
 * the custom fields in the store (the caller loads them with useGetCustomFieldsOnMount); a
 * synthetic required setting makes it behave as required, as validateUpdateRequiredFields assumes.
 */
export function AutoReviewCustomField(props: Props) {
  const { fieldId } = props
  const customFields = useSelector(selectAllCustomFields)
  const field = useMemo(
    () => customFields.find((candidate) => candidate.id === fieldId),
    [customFields, fieldId],
  )

  const fieldSetting = useMemo<ICustomFieldFormSetting>(
    () => ({
      customFieldId: fieldId,
      enabled: true,
      order: 0,
      required: true,
      name: field?.name ?? '',
    }),
    [fieldId, field?.name],
  )

  if (!field) return null

  return (
    <EditableCustomField
      field={field}
      margin="none"
      fieldSetting={fieldSetting}
      hideOptional={false}
    />
  )
}
AutoReviewCustomField.displayName = 'AutoReviewCustomField'
